import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl/src/types';

import { hasAddedLines } from '@/utils/checkout';

import type { BreadcrumbItem } from '@/components/Breadcrumb';
import { CMSSection } from '@/components/Cms/CMSSection';
import { Price } from '@/components/Price';
import { ProductDetail } from '@/components/ProductDetail';
import type { SecondaryCTAProps } from '@/components/ProductDetail/components';
import { useStickyPurchaseBar } from '@/components/ProductDetail/components';
import type { SelectOption } from '@/components/Select';
import { ServicePerks } from '@/components/ServicePerks';
import { Typography } from '@/components/Typography';
import type { ContentfulPlaceholderProductDetailsFragment } from '@/graphql/contentful/api';
import type { OrderAppProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import {
  type ProductVariantStrapBasicDetailsFragment,
  type ProductVariantStrapDetailsFragment,
} from '@/graphql/saleor/api';
import { useBucklesAndTools } from '@/hooks/useBucklesAndTools';
import { useCheckout } from '@/hooks/useCheckout';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsMdDown } from '@/hooks/useMedia';
import { useNativeCall } from '@/hooks/useNativeCall';
import { useRouter } from '@/hooks/useRouter';
import { useSendAddedToCartAlgoliaEvent } from '@/hooks/useSendAlgoliaEvent';
import { useStrapSizeDatas } from '@/hooks/useStrapSizeDatas';
import { useStrapSizes } from '@/hooks/useStrapSizes';
import { findProductCta } from '@/integrations/orderApp';
import {
  getBoolValue,
  getDropdownValue,
  getMultiselectValue,
} from '@/lib/attributes';
import { EVENT_NAME } from '@/lib/gtm/const';
import { getProductTranslatedName } from '@/lib/i18n';
import { paths } from '@/lib/paths';
import { getProductPath, getProductType } from '@/lib/product';
import { getTaxMessageShort } from '@/lib/regions';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe } from '@/lib/types';
import { makeFirstLetterUppercase } from '@/lib/util';
import { commonMessages } from '@/messages/common';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { AddToBagDrawer } from '../AddToBagDrawer';
import { Sizes } from '../Sizes';

export interface ProductDetailSectionOpts {
  businessData: Maybe<OrderAppProductVariantsBusinessDetailsQuery>;
  variantBasicDetails: ProductVariantStrapBasicDetailsFragment;
  variantDetails: Maybe<ProductVariantStrapDetailsFragment>;
}

export type ProductDetailSectionProps =
  ContentfulPlaceholderProductDetailsFragment & ProductDetailSectionOpts;

export type SizeState = {
  filteredSelectItems?: SelectOption[];
  isLoadingOtherSize: boolean;
  selectItems: SelectOption[];
  sizesSelectedItem: Maybe<SelectOption>;
};

export const ProductDetailSection = ({
  businessData,
  perksCollection,
  variantBasicDetails: { id, product, sku },
  variantBasicDetails,
  variantDetails,
}: ProductDetailSectionProps) => {
  const isMobile = useIsMdDown();
  const { country, locale, productCatalogMode } = useRegionProvider();
  const taxMessage = getTaxMessageShort(country.channelSlug);
  const { setContactUsModalStatus } = useCommonModalProviderContext();
  const { formatMessage } = useIntl();
  const [isAddToBagModalOpen, setIsAddToBagModalOpen] = useState(false);
  const [strapSizeDatas, setStrapSizeDatas] = useState<
    Record<string, any> | undefined
  >();
  const [sizeState, setSizeState] = useState<SizeState>({
    selectItems: [],
    sizesSelectedItem: null,
    isLoadingOtherSize: false,
    filteredSelectItems: [],
  });

  const [drawerSizeState, setDrawerSizeState] = useState<SizeState>({
    selectItems: [],
    sizesSelectedItem: null,
    isLoadingOtherSize: false,
    filteredSelectItems: [],
  });

  const {
    lineAdd,
    snapshot: { isProcessing },
  } = useCheckout();
  const { buckles, tools } = useBucklesAndTools(
    variantBasicDetails?.product.compatibleBuckles,
  );
  const { initiateNativeCall } = useNativeCall(country.code);
  const { sendEcommerceEventBySkus } = useGoogleAnalytics();

  const name = getProductTranslatedName(product);
  const description =
    product.marketingDescription?.values[0]?.translation?.plainText;
  const lugSize = getDropdownValue(variantBasicDetails?.product.lugSize);
  const integratedBuckle = getBoolValue(
    variantBasicDetails?.product.integratedBuckle,
  );

  const {
    query: { material },
  } = useRouter();
  const materialSlug = typeof material === 'string' ? material : '';

  const materialDisplayName = `${makeFirstLetterUppercase(
    product.strapMaterial?.values?.[0]?.translation?.plainText,
  )} ${formatMessage(commonMessages.straps)}`;
  const breadcrumbs = getBreadcrumbs(
    formatMessage,
    materialDisplayName,
    materialSlug,
  );

  const otherSizesSkus = getMultiselectValue(
    variantBasicDetails?.product.otherSizesSkus,
  );
  const skus = sku ? [sku, ...otherSizesSkus] : [];
  const productType = getProductType(product.productType);
  const cta = findProductCta(
    strapSizeDatas,
    sizeState.sizesSelectedItem
      ? (sizeState.sizesSelectedItem.data as { id: string; sku: string })?.sku
      : sku,
  );

  const sendAddedToCartAlgoliaEvent = useSendAddedToCartAlgoliaEvent();

  const sendStrapAddedToCart = useCallback(() => {
    if (!sku) return;
    sendAddedToCartAlgoliaEvent(
      sku,
      variantDetails?.pricing?.price?.gross.amount,
      variantDetails?.pricing?.price?.gross.currency,
    );
  }, [sendAddedToCartAlgoliaEvent, sku, variantDetails?.pricing?.price]);

  // BEC-24431: sticky purchase bar shown once the on-page CTA scrolls off
  // screen. Gate matches the on-page CTA exactly (rendered whenever `cta` is
  // set) so the bar mirrors every state, including Contact Us ('NO_CTA').
  const { ctaRef, visible: showStickyBar } = useStickyPurchaseBar({
    cta,
    productCatalogMode,
  });

  const handleAddToCart = useCallback(async () => {
    if (!cta || cta === 'NO_CTA') return setContactUsModalStatus(true, sku);
    if (buckles || tools) {
      setIsAddToBagModalOpen(true);
    } else if (cta === 'ORDER' || cta === 'PRE_ORDER') {
      const addedLines = await lineAdd(
        sizeState.sizesSelectedItem ? sizeState.sizesSelectedItem.value : id,
        cta,
      );
      if (hasAddedLines(addedLines)) {
        sendStrapAddedToCart();
      }
    }
  }, [
    buckles,
    cta,
    id,
    lineAdd,
    sendStrapAddedToCart,
    setContactUsModalStatus,
    sizeState.sizesSelectedItem,
    sku,
    tools,
  ]);

  const shareableProductData = {
    sku: product.defaultVariant?.sku,
    name: product.name,
    collection: product.collections?.[0]?.name,
    description:
      product.marketingDescription?.values[0]?.translation?.plainText,
    image: product.media?.[0]?.url,
    url: [
      process.env.NEXT_PUBLIC_URL,
      country.code.toLowerCase(),
      '-',
      locale.slug,
      getProductPath(product, true),
    ].join(''),
  };

  const callToBuy = () => {
    if (sku) {
      sendEcommerceEventBySkus({
        event: EVENT_NAME.CALL_TO_BUY,
        skus: [sku],
      });
    }
    initiateNativeCall();
  };

  useEffect(() => {
    if (sku) {
      sendEcommerceEventBySkus({
        event: EVENT_NAME.VIEW_ITEM,
        skus: [sku],
      });
    }
  }, [sku]);

  const defaultImageAlt = `${product.name} - ${sku}`;

  useStrapSizeDatas(setStrapSizeDatas, sku);
  // This one should be the same as Sizes.tsx, otherwise there will be a conflict.
  useStrapSizes(skus, id, false, setSizeState, sizeState, strapSizeDatas);
  // This one is needed because in the drawer we show only available sizes, and in the select all the sizes are displayed.
  useStrapSizes(
    skus,
    id,
    true,
    setDrawerSizeState,
    drawerSizeState,
    strapSizeDatas,
  );

  useEffect(() => {
    const updatedFilteredSelectItems = sizeState.selectItems.filter((item) => {
      const matchingVariant = businessData?.productVariants?.find(
        (variant: any) => variant.sku === (item.data as { sku: string }).sku,
      );
      return !matchingVariant || matchingVariant.quantityAvailable >= 1;
    });
    setSizeState((prevState) => ({
      ...prevState,
      filteredSelectItems: updatedFilteredSelectItems,
    }));
  }, [sizeState.selectItems, businessData]);

  return (
    <CMSSection>
      <ProductDetail key={sku} data-test-id='ProductDetail'>
        <ProductDetail.ImagesGallery
          alt={defaultImageAlt}
          compactOnMobile
          data-test-id='ProductDetailImagesGallery'
          gallery='strapPdpCarousel'
          media={product.media}
          mediaLarge={product.mediaLarge}
          name={name}
          price={variantDetails?.pricing?.price?.gross.amount}
          productType={productType}
          shareableProductData={shareableProductData}
          sku={sku}
          theCurrency={variantDetails?.pricing?.price?.gross.currency}
        >
          <ProductDetail.Breadcrumbs
            data-test-id='ProductDetailBreadcrumbsMdHidden'
            items={breadcrumbs}
            mdHidden
          />
        </ProductDetail.ImagesGallery>

        <ProductDetail.DetailsCard>
          <ProductDetail.Breadcrumbs
            data-test-id='ProductDetailBreadcrumbsMdVisible'
            items={breadcrumbs}
            mdVisible
            withJsonLd={false}
          />
          <ProductDetail.Sku data-test-id='ProductDetailSku'>
            {sizeState.sizesSelectedItem
              ? (
                  sizeState.sizesSelectedItem.data as {
                    id: string;
                    sku: string;
                  }
                )?.sku
              : sku}
          </ProductDetail.Sku>
          <ProductDetail.Name $compactOnMobile data-test-id='ProductDetailName'>
            {name} {lugSize && `- ${lugSize} MM`}
          </ProductDetail.Name>
          {description && !isMobile && (
            <ProductDetail.Description
              data-test-id='ProductDetailDescription'
              description={description}
            />
          )}
          <ProductDetail.Divider mdVisible={true} />
          {variantDetails?.pricing?.price?.gross && (
            <ProductDetail.Pricing
              $compactOnMobile
              data-test-id='ProductDetailPricing'
            >
              <Price price={variantDetails?.pricing?.price?.gross} size='xl' />
              <ProductDetail.PriceInformation loading={!variantDetails} />
              <ProductDetail.TaxLabel data-test-id='ProductDetailTaxLabel'>
                {taxMessage && formatMessage(taxMessage)}
              </ProductDetail.TaxLabel>
            </ProductDetail.Pricing>
          )}
          {!integratedBuckle && (
            <Typography
              color='secondaryGrey'
              data-test-id='ProductDetailBuckle'
              marginBottom={2}
              scale='p2'
              {...generateDataTestId('productDetail', 'buckle')}
            >
              <FormattedMessage {...commonMessages.buckleNotIncluded} />
            </Typography>
          )}
          {!!otherSizesSkus.length && (
            <Sizes
              businessData={businessData}
              setSizeState={setSizeState}
              sizeState={sizeState}
              sizesSkus={[sku!, ...otherSizesSkus]}
              sku={sku ?? ''}
              strapId={id}
            />
          )}
          {!productCatalogMode && (
            <div ref={ctaRef}>
              <ProductDetail.CTA
                cta={cta}
                data-test-id='ProductDetailAddToBagCta'
                isProcessing={isProcessing || sizeState.isLoadingOtherSize}
                loading={!businessData || sizeState.isLoadingOtherSize}
                onClick={handleAddToCart}
                {...generateDataTestId('productDetail', 'addToBagCta')}
              />
            </div>
          )}
          {variantDetails && variantDetails?.pricing?.price && businessData && (
            <AddToBagDrawer
              buckles={buckles}
              businessData={businessData}
              cta={cta}
              isOpen={isAddToBagModalOpen}
              niceToHaveProducts={tools}
              onAddedToBag={sendStrapAddedToCart}
              setIsOpen={setIsAddToBagModalOpen}
              sizeState={drawerSizeState}
              strap={{
                name: { [locale.code.toUpperCase()]: product.name },
                currency: variantDetails.pricing.price.gross.currency,
                thumbnail_url: product.thumbnail?.url,
                price: variantDetails.pricing.price.gross.amount,
                integrated_buckle: integratedBuckle,
              }}
              strapId={
                drawerSizeState.sizesSelectedItem
                  ? drawerSizeState.sizesSelectedItem.value
                  : id
              }
              strapPrice={variantDetails.pricing.price}
              strapSku={
                drawerSizeState.sizesSelectedItem
                  ? (
                      drawerSizeState.sizesSelectedItem.data as {
                        id: string;
                        sku: string;
                      }
                    )?.sku
                  : sku
              }
            />
          )}
          {!productCatalogMode && (
            <>
              <ProductDetail.SecondaryCTA
                data-test-id='ProductDetailSecondaryCta'
                items={SECONDARY_CTA_DATA.map((item) => ({
                  ...item,
                  onClick: callToBuy,
                }))}
              />
              <ServicePerks
                data-test-id='ServicePerksMdVisibleDesktop'
                mdVisible
                perksCollection={perksCollection}
                variant='desktop'
              />
              <ServicePerks
                data-test-id='ServicePerksMdHiddenMobile'
                mdHidden
                perksCollection={perksCollection}
                variant='mobile'
              />
            </>
          )}
          {description && isMobile && (
            <ProductDetail.Description
              data-test-id='ProductDetailDescription'
              description={description}
            />
          )}
        </ProductDetail.DetailsCard>
      </ProductDetail>
      <ProductDetail.StickyPurchaseBar
        cta={cta}
        image={product.media?.[0]}
        isProcessing={isProcessing || sizeState.isLoadingOtherSize}
        loading={!businessData || sizeState.isLoadingOtherSize}
        name={name}
        onClick={handleAddToCart}
        price={variantDetails?.pricing?.price?.gross}
        visible={showStickyBar}
      />
    </CMSSection>
  );
};

const getBreadcrumbs = (
  formatMessage: IntlShape['formatMessage'],
  materialDisplayName: string,
  materialSlug: string,
): BreadcrumbItem[] => {
  return [
    { label: formatMessage(commonMessages.home), target: paths.url() },
    {
      label: formatMessage(commonMessages.straps),
      target: paths.straps.url(),
    },
    {
      label: materialDisplayName,
      target: paths.straps.material.url({ material: materialSlug }),
    },
  ];
};

const SECONDARY_CTA_DATA: SecondaryCTAProps['items'] = [
  {
    icon: 'telephoneCall',
    id: 'callToBuy',
    message: <FormattedMessage {...commonMessages.callToBuy} />,
  },
];
